/**
 * VENDOR MODULES
 */
import React from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { Tab, Disclosure, Transition } from '@headlessui/react';

import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';
import { getMarketTypeByGroupId } from 'shared/utils/markets';
import {
  pickOutcome,
  checkOutcomeInTicket,
} from 'shared/utils/ticket-slip-helper';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useSchedulesQuery, useFootball3dWeeksAndEventsQuery, useFootball3dScheduleResultsQuery } from 'features/events/queries';
import { useFootball3dScheduleId } from 'features/events/hooks/useFootball3dScheduleId';
import Football3dArea, { FOOTBALL3D_YELLOW } from 'features/markets/components/bets-area/Football3dArea';
import FootballStandingsTab from 'features/markets/components/bets-area/FootballStandingsTab';
import FootballScheduleResultsTab from 'features/markets/components/bets-area/FootballScheduleResultsTab';
import FootballWeekResultsCardGrid from 'features/markets/components/bets-area/FootballWeekResultsCardGrid';
import { getTeamCode } from 'shared/utils/teamDisplay';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { useServerNow } from 'shared/queries/serverTime';
import { getEventEndMsFromResults } from 'features/results/eventEndFromResults';
import { useEventResultsQuery } from 'features/results/queries';
import { ClosedEventPanel } from 'features/results/components/ClosedEventPanel';
import { getSportColor, getSportLightColor, getSportOnColor } from 'shared/utils/sport-colors';
import { UserSessionContext } from 'features/session/userSessionStore';
import CompactOddChip from 'shared/components/outcomes/CompactOddChip';
import { useTicketSlipStore } from 'features/tickets/store';
import { RtpTransparencyModal } from 'features/rtp/components/RtpTransparencyModal';
import { Button } from 'shared/components/ui/button';
import { cn } from 'lib/utils';
import { liveUntilSecFromEventLike } from 'features/realtime/liveFromResults';
import { useOngoingEventQuery } from 'features/video/queries';

/** market_type_id -> market_type_group_id for Football 3D main markets */
const MARKET_GROUP_BY_TYPE = { 5001: 508, 5002: 509, 5003: 510 };

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(Number(ts) * 1000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function getEventKey(event, idx) {
  const id = event?.event_id;
  if (id != null) return String(id);
  const ts = event?.event_timestamp;
  const home = event?.home_team_code || event?.home_team?.code || '';
  const away = event?.away_team_code || event?.away_team?.code || '';
  return `event-${String(ts ?? 'na')}-${String(home)}-${String(away)}-${String(idx)}`;
}

function Football3dWeekResultsBoard({ weekNumber, events, resultsByEventId }) {
  const { t } = useTranslation();

  const rows = React.useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    return list.map((event, idx) => {
      const eventId = event?.event_id != null ? String(event.event_id) : getEventKey(event, idx);
      const result = resultsByEventId?.[eventId] ?? null;
      const homeCode = getTeamCode(event?.home_team, event?.home_team_code) || t('HOME');
      const awayCode = getTeamCode(event?.away_team, event?.away_team_code) || t('AWAY');
      return {
        event,
        eventId,
        featured: Boolean(event?.featured),
        result,
        homeCode,
        awayCode,
        homeLogoUrl: homeCode ? getFootball3dTeamLogoUrl(homeCode) : '',
        awayLogoUrl: awayCode ? getFootball3dTeamLogoUrl(awayCode) : '',
        scoreLabel:
          result != null &&
          result.home_score != null &&
          result.away_score != null
            ? `${Number(result.home_score)} - ${Number(result.away_score)}`
            : '—',
        eventTimestamp: event?.event_timestamp,
      };
    });
  }, [events, resultsByEventId, t]);

  return (
    <div className="w-full">
      <FootballWeekResultsCardGrid
        rows={rows}
        showWeekHeader
        weekNumber={weekNumber}
      />
    </div>
  );
}

function Football3dWeekEventRow(props) {
  const { event, idx, weekNumber, weekStartTime, scheduleId, resultsOverride } = props;
  const { t } = useTranslation();
  const [{ user }] = React.useContext(UserSessionContext);
  const { data: schedules = [] } = useSchedulesQuery();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const addTip = useTicketSlipStore((s) => s.addTip);
  const oddsFormat = user?.odds_format || 'decimal';

  const serverNowMs = useServerNow();
  const eventId = event?.event_id;
  const eventStartMs = event?.event_timestamp ? Number(event.event_timestamp) * 1000 : null;
  const hasStarted = eventStartMs != null && Number.isFinite(eventStartMs) && serverNowMs >= eventStartMs;

  const eventResultsQuery = useEventResultsQuery({
    eventId,
    scheduleId,
    enabled: !!eventId && hasStarted && !(resultsOverride && typeof resultsOverride === 'object' && Object.keys(resultsOverride).length > 0),
  });
  const realtimeResults = (resultsOverride && typeof resultsOverride === 'object' && Object.keys(resultsOverride).length > 0)
    ? resultsOverride
    : eventResultsQuery.data;
  const hasResults = !!realtimeResults && typeof realtimeResults === 'object' && Object.keys(realtimeResults || {}).length > 0;
  const eventEndMs = getEventEndMsFromResults(realtimeResults ?? null);
  const eventHasEnded =
    (resultsOverride && typeof resultsOverride === 'object' && Object.keys(resultsOverride).length > 0) ||
    (eventEndMs != null && Number.isFinite(eventEndMs) && serverNowMs >= eventEndMs);
  const showResults = hasResults && eventHasEnded;

  const home = getTeamCode(event?.home_team, event?.home_team_code) || t('HOME');
  const away = getTeamCode(event?.away_team, event?.away_team_code) || t('AWAY');
  const homeTeamCode = getTeamCode(event?.home_team, event?.home_team_code);
  const awayTeamCode = getTeamCode(event?.away_team, event?.away_team_code);
  const homeLogoUrl = homeTeamCode ? getFootball3dTeamLogoUrl(homeTeamCode) : '';
  const awayLogoUrl = awayTeamCode ? getFootball3dTeamLogoUrl(awayTeamCode) : '';
  const time = formatTime(event?.event_timestamp);
  const week = event?.week_number ?? weekNumber;
  const defaultOpen = idx === 0;

  const handleHeaderOddClick = React.useCallback(
    (e, outcomeObj) => {
      e.stopPropagation();
      if (!outcomeObj?.odd || hasStarted) return;
      const market_type_id = parseInt(outcomeObj.market_type_id, 10);
      const market_type_group_id = MARKET_GROUP_BY_TYPE[market_type_id];
      if (!Number.isFinite(market_type_group_id)) return;
      const eventForBet = { ...event, outcomes: event?.outcomes };
      const picked = pickOutcome({
        event: eventForBet,
        pick_outcome: outcomeObj.outcome,
        market_type_id,
      });
      if (!picked) return;
      const marketTypeData = getMarketTypeByGroupId(market_type_group_id, { ...event, market_types: event?.market_types }) ?? {};
      const market_name = marketTypeData.name ?? '';
      const sport_id = event?.sport_id;
      if (sport_id == null) return;
      const scheduleWithSport = Array.isArray(schedules) ? schedules.find((s) => s.sport?.sport_id == sport_id) : null;
      const sport_alias = scheduleWithSport?.sport?.alias ?? '';
      const tip = {
        ...picked,
        stake: 1.0,
        market_type_group_id,
        market_name,
        sport_alias,
      };
      if (checkOutcomeInTicket(tip.outcome_id, ticket_slip)) {
        Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
          title: i18n.t('DOUBLE_BET'),
          message: i18n.t('EXISTING_OUTCOME'),
        });
        return;
      }
      addTip(tip);
    },
    [event, hasStarted, ticket_slip, addTip, schedules]
  );

  const statusChip = showResults
    ? { label: t('EVENT_FINISHED') || 'Finished', className: 'bg-gray-100 text-muted-foreground' }
    : hasStarted
      ? { label: t('LIVE') || 'LIVE', className: 'bg-red-100 text-red-700' }
      : null;

  const outcomesList = Array.isArray(event?.outcomes) ? event.outcomes : (event?.outcomes && typeof event.outcomes === 'object' ? Object.values(event.outcomes) : []);
  const byMarket = React.useMemo(() => {
    const m = new Map();
    for (const o of outcomesList) {
      const mt = parseInt(o?.market_type_id, 10);
      if (!Number.isFinite(mt)) continue;
      if (!m.has(mt)) m.set(mt, []);
      m.get(mt).push(o);
    }
    return m;
  }, [outcomesList]);

  const odds1x2 = byMarket.get(5001) || [];
  const oddsDoubleChance = byMarket.get(5002) || [];
  const oddsGoalNoGoal = byMarket.get(5003) || [];
  const home1 = odds1x2.find((o) => o.outcome === 'HOME');
  const draw1 = odds1x2.find((o) => o.outcome === 'DRAW');
  const away1 = odds1x2.find((o) => o.outcome === 'AWAY');
  const dc1x = oddsDoubleChance.find((o) => String(o.outcome) === 'HOME_DRAW');
  const dc12 = oddsDoubleChance.find((o) => String(o.outcome) === 'HOME_AWAY');
  const dcx2 = oddsDoubleChance.find((o) => String(o.outcome) === 'DRAW_AWAY');
  const under25 = oddsGoalNoGoal.find((o) => String(o.outcome) === 'UNDER_2.5' || String(o.outcome) === 'UNDER_2');
  const over25 = oddsGoalNoGoal.find((o) => String(o.outcome) === 'OVER_2.5' || String(o.outcome) === 'OVER_2');
  const hasHeaderOdds = (home1?.odd != null || draw1?.odd != null || away1?.odd != null) ||
    (dc1x?.odd != null || dc12?.odd != null || dcx2?.odd != null) ||
    (under25?.odd != null || over25?.odd != null);

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div>
          <Disclosure.Button
            className={cn(
              'w-full text-left cursor-pointer select-none px-3 py-3',
              showResults ? 'bg-muted/30' : 'c7-sport-tint'
            )}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1 flex items-center gap-2">
                {homeLogoUrl && (
                  <img src={homeLogoUrl} alt="" className="w-8 h-8 shrink-0 object-contain rounded" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-sm md:text-base truncate text-gray-900" title={`${home} vs ${away}`}>
                    {home} <span className="opacity-70">vs</span> {away}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(t('WEEK_LABEL') || 'Week')} {week}{time ? ` • ${time}` : ''}{eventId ? ` • #${eventId}` : ''}
                  </div>
                </div>
                {awayLogoUrl && (
                  <img src={awayLogoUrl} alt="" className="w-8 h-8 shrink-0 object-contain rounded" />
                )}
              </div>
              {hasHeaderOdds && !showResults && (
                <div className="hidden lg:flex flex-wrap items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {[
                    [home1, '1'],
                    [draw1, 'X'],
                    [away1, '2'],
                    [under25, t('UNDER') || 'U'],
                    [over25, t('OVER') || 'O'],
                    [dc1x, '1X'],
                    [dc12, '12'],
                    [dcx2, 'X2'],
                  ].filter(([o]) => o?.odd != null).map(([outcomeObj, label]) => (
                    <span
                      key={label}
                      role="button"
                      tabIndex={0}
                      aria-label={t('ADD_TO_BETSLIP') || 'Add to betslip'}
                      aria-disabled={hasStarted}
                      onClick={(e) => handleHeaderOddClick(e, outcomeObj)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!hasStarted) handleHeaderOddClick(e, outcomeObj);
                        }
                      }}
                      className={cn(
                        'cursor-pointer rounded inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                        hasStarted && 'cursor-not-allowed opacity-60'
                      )}
                    >
                      <CompactOddChip
                        odd={outcomeObj.odd}
                        label={label}
                        oddsFormat={oddsFormat}
                        isSelected={checkOutcomeInTicket(outcomeObj.outcome_id, ticket_slip)}
                      />
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {statusChip && (
                  <span className={cn('inline-flex items-center justify-center rounded-md px-2 py-1 text-xs font-semibold', statusChip.className)}>
                    {statusChip.label}
                  </span>
                )}
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border bg-background/60 text-foreground transition-transform duration-200"
                  aria-hidden="true"
                  style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <i className="bi bi-chevron-down" />
                </span>
              </div>
            </div>
          </Disclosure.Button>

          <Transition
            show={open}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Disclosure.Panel static>
              <div className="c7-sport-tint c7-panel overflow-hidden">
                {showResults ? (
                    <ClosedEventPanel
                      eventId={eventId}
                      sportId={50}
                      scheduleId={scheduleId}
                      resultsOverride={realtimeResults}
                      weekNumber={weekNumber}
                      weekStartTime={weekStartTime}
                    />
                  ) : (
                    <>
                      <Football3dArea event={event} showHeader={false} />
                    </>
                  )}
                </div>
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
}

/**
 * Football3D week-scoped markets.
 *
 * Renders a list of events for the selected week; each event clearly scopes its outcomes.
 */
function Football3dWeekArea() {
  const { t } = useTranslation();
  const scheduleId = useFootball3dScheduleId();
  const { selectedWeekId, setSelectedWeekId } = useScheduleContext();
  const [isRtpOpen, setIsRtpOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Single query: weeks + all events by week (Football3D).
  const ongoingQuery = useOngoingEventQuery({ scheduleId, enabled: !!scheduleId });
  const football3dQuery = useFootball3dWeeksAndEventsQuery({ scheduleId });
  const { data: football3dScheduleResults = [] } = useFootball3dScheduleResultsQuery({ scheduleId });
  const { refetch: refetchFootball3d, isRefetching: isRefetchingFootball3d } = football3dQuery;
  const weeks = football3dQuery.data?.weeks ?? [];

  const weekKickoffByWeek = React.useMemo(() => {
    const map = new Map();
    for (const w of weeks) {
      const list = Array.isArray(w.events) ? w.events : [];
      const ts = list
        .map((e) => Number(e?.event_timestamp))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)[0];
      if (ts) map.set(w.week_id, ts);
    }
    return map;
  }, [weeks]);

  const sortedWeeks = React.useMemo(() => {
    return [...weeks].sort((a, b) => {
      const ta = weekKickoffByWeek.get(a.week_id);
      const tb = weekKickoffByWeek.get(b.week_id);
      const aHas = ta != null && Number.isFinite(Number(ta)) ? 1 : 0;
      const bHas = tb != null && Number.isFinite(Number(tb)) ? 1 : 0;
      if (aHas && bHas) {
        if (Number(ta) !== Number(tb)) return Number(ta) - Number(tb);
        return a.week_id - b.week_id;
      }
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return a.week_id - b.week_id;
    });
  }, [weeks, weekKickoffByWeek]);

  React.useEffect(() => {
    if (!sortedWeeks.length) return;
    if (selectedWeekId == null) {
      const liveWeekId =
        ongoingQuery.data?.week_id != null && sortedWeeks.some(w => w.week_id === Number(ongoingQuery.data.week_id))
          ? Number(ongoingQuery.data.week_id)
          : null;
      setSelectedWeekId(liveWeekId ?? sortedWeeks[0].week_id);
    }
  }, [scheduleId, sortedWeeks, selectedWeekId, setSelectedWeekId, ongoingQuery.data?.week_id]);

  const selectedWeekNumber = sortedWeeks.find(w => w.week_id === selectedWeekId)?.week_number ?? selectedWeekId;

  const events = React.useMemo(() => {
    if (selectedWeekId == null) return [];
    const weekObj = weeks.find(w => w.week_id === selectedWeekId);
    const list = Array.isArray(weekObj?.events) ? weekObj.events : [];

    const deduped = [];
    const seen = new Set();
    for (const e of list) {
      const eventId = e?.event_id != null ? String(e.event_id) : null;
      if (!eventId || seen.has(eventId)) continue;
      const w = e?.week_id != null ? Number(e.week_id) : selectedWeekId;
      if (Number.isFinite(selectedWeekId) && Number.isFinite(w) && w !== selectedWeekId) continue;
      seen.add(eventId);
      deduped.push(e);
    }

    deduped.sort((a, b) => {
      const aF = Boolean(a?.featured);
      const bF = Boolean(b?.featured);
      if (aF !== bF) return aF ? -1 : 1;
      const ta = a?.event_timestamp;
      const tb = b?.event_timestamp;
      if (ta == null || tb == null) return 0;
      return Number(ta) - Number(tb);
    });
    return deduped.slice(0, 10);
  }, [selectedWeekId, weeks]);

  const list = Array.isArray(events) ? events : [];
  const serverNowMs = useServerNow();
  const serverNowSec = serverNowMs / 1000;

  /** Map event_id -> full result for the selected week (from single schedule-results call). */
  const resultsByEventId = React.useMemo(() => {
    const map = {};
    if (selectedWeekId == null) return map;
    const results = Array.isArray(football3dScheduleResults) ? football3dScheduleResults : [];
    for (const r of results) {
      if (r?.event_id == null) continue;
      if (Number(r.week_id) !== selectedWeekId) continue;
      map[String(r.event_id)] = r;
    }
    return map;
  }, [selectedWeekId, football3dScheduleResults]);

  const weekEnded = React.useMemo(() => {
    if (selectedWeekId == null || list.length === 0) return false;
    return list.every((ev) => {
      const liveUntilSec = liveUntilSecFromEventLike(ev);
      return liveUntilSec != null && Number.isFinite(liveUntilSec) && serverNowSec >= liveUntilSec;
    });
  }, [selectedWeekId, list, serverNowSec]);

  const weekIsLive = React.useMemo(() => {
    if (selectedWeekId == null || list.length === 0) return false;
    return list.some((ev) => {
      const start = ev?.event_timestamp != null ? Number(ev.event_timestamp) : null;
      const liveUntil = liveUntilSecFromEventLike(ev);
      if (start == null || !Number.isFinite(start)) return false;
      if (serverNowSec < start) return false;
      if (liveUntil != null && Number.isFinite(liveUntil) && serverNowSec >= liveUntil) return false;
      return true;
    });
  }, [selectedWeekId, list, serverNowSec]);

  const isLoading = football3dQuery.isLoading;

  if (football3dQuery.isError) {
    return (
      <div className="p-4 text-sm text-destructive flex flex-col gap-2">
        <span>{t('ERROR_LOADING_DATA') || 'Failed to load data.'}</span>
        <button
          type="button"
          onClick={() => refetchFootball3d()}
          disabled={isRefetchingFootball3d}
          className="self-start px-3 py-1.5 rounded-md text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefetchingFootball3d ? (t('LOADING') || 'Loading…') : (t('RETRY') || 'Try again')}
        </button>
      </div>
    );
  }

  if (!football3dQuery.isLoading && sortedWeeks.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-600">
        {t('NO_EVENTS') || 'No events'}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-gray-600">
        {t('LOADING') || 'Loading…'}
      </div>
    );
  }

  // Week is selected via the week selector (like event selection for other sports).
  // When weeks are ready but none selected yet, prompt user; otherwise show loading.
  if (!selectedWeekId) {
    return (
      <div className="p-4 text-sm text-gray-600">
        {sortedWeeks.length > 0
          ? (t('SELECT_A_WEEK') || 'Select a week above')
          : (t('LOADING') || 'Loading…')}
      </div>
    );
  }

  const sportColor = getSportColor(50);
  const sportLightColor = getSportLightColor(50);
  const sportOnColor = getSportOnColor(50);

  if (weekIsLive) {
    return (
      <div className="p-3 border-b border-border bg-warning/20 text-warning-foreground text-sm font-semibold">
        {t('EVENT_RUNNING_BETTING_DISABLED') || 'Event is running. Betting is closed.'}
      </div>
    );
  }

  const eventListContent = (
    <>
      {list.length === 0 ? (
        <div className="p-4 text-sm text-gray-600">
          {t('NO_EVENTS') || 'No events'}
        </div>
      ) : (
        <>
          <div className="p-3 border-b border-border c7-sport-gradient flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[var(--sport-on-color)] font-semibold">
                {(t('WEEK_LABEL') || 'Week')} {selectedWeekNumber}
              </div>
              <div className="text-[var(--sport-on-color)]/80 text-xs">
                {weekEnded
                  ? (t('RESULTS') || 'Results')
                  : (t('SELECT_EVENT_MARKETS') || 'Select outcomes inside each match below.')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsRtpOpen(true)}
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-black/10 hover:bg-black/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-background text-[var(--sport-color)]"
              aria-label={t('RTP_TRANSPARENCY_TITLE') || 'Winning probability (RTP)'}
              title={t('RTP_TRANSPARENCY_TITLE') || 'Winning probability (RTP)'}
            >
              <i className="bi bi-info-circle text-lg" aria-hidden="true" />
            </button>
          </div>
          <RtpTransparencyModal
            open={isRtpOpen}
            onClose={() => setIsRtpOpen(false)}
            scheduleId={scheduleId}
            sportId={50}
          />
          <div className="divide-y divide-border">
            {list.map((event, idx) => (
              <Football3dWeekEventRow
                key={getEventKey(event, idx)}
                event={event}
                idx={idx}
                weekNumber={selectedWeekNumber}
                weekStartTime={weekKickoffByWeek.get(selectedWeekId)}
                scheduleId={scheduleId}
                resultsOverride={resultsByEventId[event?.event_id]}
              />
            ))}
          </div>
        </>
      )}
    </>
  );

  if (weekEnded) {
    return (
      <div className="w-full min-w-0" style={{ '--sport-color': sportColor, '--sport-light-color': sportLightColor, '--sport-on-color': sportOnColor }}>
        <Football3dWeekResultsBoard
          weekNumber={selectedWeekNumber}
          events={list}
          resultsByEventId={resultsByEventId}
        />
      </div>
    );
  }

  const weekTabClass = (selected) => cn(
    'py-2 md:py-3 text-center cursor-pointer transition-colors text-xs md:text-sm rounded-md border-[3px]',
    selected
      ? 'border-[var(--sport-color)] bg-card text-foreground'
      : 'border-border bg-card text-foreground hover:border-[var(--sport-color)]'
  );

  const tabPanelClass =
    'w-full min-w-0 min-h-0 flex-1 flex flex-col overflow-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset focus-visible:ring-offset-0 rounded';

  return (
    <div className="w-full min-w-0" style={{ '--sport-color': sportColor, '--sport-light-color': sportLightColor, '--sport-on-color': sportOnColor }}>
      <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex} as="div" className="w-full min-w-0 flex flex-col">
        <Tab.List className="grid grid-cols-3 gap-1 py-2 border-b border-border w-full min-w-0">
          <Tab className={({ selected }) => weekTabClass(selected)}>
            <span className="truncate block px-1 font-bold uppercase">{t('MARKETS') || 'Markets'}</span>
          </Tab>
          <Tab className={({ selected }) => weekTabClass(selected)}>
            <span className="truncate block px-1 font-bold uppercase">{t('STANDINGS') || 'Standings'}</span>
          </Tab>
          <Tab className={({ selected }) => weekTabClass(selected)}>
            <span className="truncate block px-1 font-bold uppercase">{t('RESULTS') || 'Results'}</span>
          </Tab>
        </Tab.List>
        <Tab.Panels className="w-full min-w-0 flex-1 min-h-0">
          <Tab.Panel className={tabPanelClass}>
            {eventListContent}
          </Tab.Panel>
          <Tab.Panel className={tabPanelClass}>
            <FootballStandingsTab />
          </Tab.Panel>
          <Tab.Panel className={cn(tabPanelClass, 'min-h-[200px]')}>
            {selectedIndex === 2 ? <FootballScheduleResultsTab /> : null}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

export default Football3dWeekArea;

